import * as React from "react";
import {
    ClientStatus,
    ICalculateFeeResponse,
    IClient,
    IFeesCalculated,
    ISubmitCalculateClientFee,
} from "intake_shared/src/types";
import { Button } from "../../../../bits/Button/Button";
import styles from "./CalculateFee.module.scss";
import { convertArrayToString } from "../../../../../helpers";
import {
    copyToClipboard,
    showMoneyInAppFormat,
} from "../../../../../../utils/utils";
import { calculateClientFees } from "../../../../../../store/client";
import { useToasts } from "react-toast-notifications";
import { Select } from "../../../../bits/Select/Select";
import { Input } from "../../../../bits/Input/Input";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import CopyIcon from "../../../../icons/Copy";
import { AlertDialog } from "../../../../bits/AlertDialog/AlertDialog";

type Option = {
    label: string;
    value: string;
};

const CalculateFeesInsuranceProviders = [
    "Aetna/Meritain",
    "Beacon GHI",
    "Beacon GHI National",
    "BCBS",
    "NYSHIP",
    "Cigna/Wellfleet",
    "UMR",
    "Harvard Pilgrim",
    "Oxford",
    "UHC",
    "UHC-Student",
];

const customerStatusOptions = [
    { label: "New", value: "new" },
    { label: "Existing", value: "existing" },
];

const yearOptions = [
    { label: "2024", value: "2024" },
    { label: "OLD 2024", value: "OLD 2024" },
    { label: "2023", value: "2023" },
];

const getInsuranceProviderOptions = [
    ...Object.values(CalculateFeesInsuranceProviders)
        .sort((a, b) => a.localeCompare(b))
        .map((provider) => ({
            label: provider,
            value: provider,
        })),
];

const findMatchingOption = (
    selectedProvider: string,
    options: Option[]
): string | undefined => {
    const lowerCasedSelectedProvider = selectedProvider?.toLowerCase();

    // Try to find an exact match first
    const exactMatch = options?.find(
        (option) => option?.label?.toLowerCase() === lowerCasedSelectedProvider
    );

    if (exactMatch) return exactMatch.label;

    // Go back to partial word match
    const foundOption = options?.find((option) =>
        lowerCasedSelectedProvider
            ?.split(" ")
            ?.some((word) => option?.label?.toLowerCase()?.includes(word))
    );

    return foundOption?.label;
};

interface CalculateFeeProps {
    client: IClient;
    setFeesCalculated: React.Dispatch<
        React.SetStateAction<IFeesCalculated | undefined>
    >;
    setAutoFillFees: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function CalculateFee({
    client,
    setFeesCalculated,
    setAutoFillFees,
}: CalculateFeeProps) {
    const { addToast } = useToasts();
    const [loading, setLoading] = React.useState(false);

    const [showCalculateFeeModal, setShowCalculateFeeModal] = React.useState(false);
    const [showAlertDialog, setShowAlertDialog] = React.useState(false);
    const insuranceVerification = client[ClientStatus.INS_VERIFICATION];
    const [selectedYear, setSelectedYear] = React.useState("2024");
    const [calculateFeeDetails, setCalculateFeesDetails] = React.useState({
        deductible: "",
        deductible_met: "",
        customer_status: "new",
        insurance:
            findMatchingOption(
                client?.insurance_provider,
                getInsuranceProviderOptions
            ) || "",
        inn_coinsurance: insuranceVerification?.INNCoins || "",
        inn_copay: insuranceVerification?.INNCo || "",
        oon_coinsurance: insuranceVerification?.OONCoins || "",
        oon_copay: insuranceVerification?.OONCo || "",
        ucr: insuranceVerification?.UCR || "",
    });

    const [calculateFeeResult, setCalculateFeeResult] =
        React.useState<ICalculateFeeResponse>();

    const handleCustomerSelect = (selectedStatus: string) => {
        setCalculateFeesDetails((prevDetails) => ({
            ...prevDetails,
            customer_status: selectedStatus,
        }));
    };

    const handleInsuranceProviderSelect = (selectedInsuranceProvider: string) => {
        setCalculateFeesDetails((prevDetails) => ({
            ...prevDetails,
            insurance: selectedInsuranceProvider,
        }));
    };

    const handleCalculateFees = async () => {
        setLoading(true);

        const dataToSend: ISubmitCalculateClientFee = {
            year: selectedYear,
            fee_params: [
                {
                    ...calculateFeeDetails,
                },
            ],
        };

        try {
            const response = await calculateClientFees(dataToSend);
            setCalculateFeeResult(response);
            // Create an object that includes the client_id and the calculation result
            const resultWithClientId: IFeesCalculated = {
                client_id: client.id,
                ...response,
            };
            setFeesCalculated(resultWithClientId);
        } catch (error) {
            addToast(`Error calculating fees: ${error.message}`, {
                appearance: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const shouldDisableButton = () => {
        // Check if all fields are empty or if insurance is empty
        const allFieldsEmpty =
            !calculateFeeDetails.deductible &&
            !calculateFeeDetails.deductible_met &&
            insuranceVerification &&
            !calculateFeeDetails.inn_coinsurance &&
            !calculateFeeDetails.inn_copay &&
            !calculateFeeDetails.oon_coinsurance &&
            !calculateFeeDetails.oon_copay &&
            !calculateFeeDetails.ucr;

        const insuranceEmpty = !client.insurance_provider;

        // Return true if all fields are empty or if only insurance is empty
        return allFieldsEmpty || insuranceEmpty;
    };

    const handleFeeCopied = (feeType: "masters" | "doctoral") => {
        if (feeType === "masters") {
            const feeToCopy =
                calculateFeeResult?.data?.[0]?.masters ?? showMoneyInAppFormat(0);

            const copiedText = convertArrayToString([`${feeToCopy}`]);
            copyToClipboard(copiedText);
        } else {
            const feeToCopy =
                calculateFeeResult?.data?.[0]?.doctoral ?? showMoneyInAppFormat(0);
            const copiedText = convertArrayToString([`${feeToCopy}`]);
            copyToClipboard(copiedText);
        }
    };

    return (
        <>
            <Dialog
                open={showCalculateFeeModal}
                onClose={() => setShowCalculateFeeModal(false)}
                title="Calculate Fees"
                variant="center"
                saveText="Calculate Fees"
                isSubmitBtnDisabled={shouldDisableButton() || loading}
                onSave={handleCalculateFees}
                onCancel={() => setShowCalculateFeeModal(false)}
            >
                <form className="v2-form">
                    <Select
                        label="Insurance"
                        options={[
                            { label: "Select option", value: "" },
                            ...getInsuranceProviderOptions,
                        ]}
                        onChange={(val) =>
                            handleInsuranceProviderSelect((val as Option).value)
                        }
                        value={getInsuranceProviderOptions.find(
                            (item) => item.value === calculateFeeDetails.insurance
                        )}
                    />
                    <div className="gridTwo">
                        <Select
                            label="Customer Status"
                            options={[
                                { label: "Select option", value: "" },
                                ...customerStatusOptions,
                            ]}
                            onChange={(val) =>
                                handleCustomerSelect((val as Option).value)
                            }
                            value={customerStatusOptions.find(
                                (item) =>
                                    item.value ===
                                    calculateFeeDetails.customer_status
                            )}
                        />
                        <Select
                            label="Year"
                            options={[
                                { label: "Select option", value: "" },
                                ...yearOptions,
                            ]}
                            onChange={(val) =>
                                setSelectedYear((val as Option).value)
                            }
                            value={yearOptions.find(
                                (item) => item.value === selectedYear
                            )}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="Deductible"
                            value={calculateFeeDetails.deductible}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    deductible: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />

                        <Input
                            label="Deductible Met"
                            value={calculateFeeDetails.deductible_met}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    deductible_met: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="INNCOINS"
                            value={calculateFeeDetails.inn_coinsurance}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    inn_coinsurance: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                        <Input
                            label="INNCOPAY"
                            value={calculateFeeDetails.inn_copay}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    inn_copay: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="OONCOINS"
                            value={calculateFeeDetails.oon_coinsurance}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    oon_coinsurance: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                        <Input
                            label="OONCOPAY"
                            value={calculateFeeDetails.oon_copay}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    oon_copay: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="UCR"
                            value={calculateFeeDetails.ucr}
                            onChange={(val) =>
                                setCalculateFeesDetails({
                                    ...calculateFeeDetails,
                                    ucr: val.target.value,
                                })
                            }
                            placeholder={showMoneyInAppFormat(0)}
                        />
                    </div>
                </form>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="470"
                    height="4"
                    viewBox="0 0 499 4"
                    fill="none"
                >
                    <path
                        d="M0 2L499 1.99996"
                        stroke="#E7E7E7"
                        strokeWidth="4"
                        strokeDasharray="2 2"
                    />
                </svg>
                <div className={styles.feeSection}>
                    <p className={styles.feeLabel}>Masters Fee</p>
                    <div className={styles.feeContent}>
                        <div className={styles.feeAmount}>
                            {calculateFeeResult?.data?.[0]?.masters
                                ? calculateFeeResult?.data?.[0]?.masters
                                : showMoneyInAppFormat(0)}
                        </div>
                        <Button
                            onClick={() => handleFeeCopied("masters")}
                            aria-label="copyMastersFee"
                            variant="normal"
                            className={styles.copyButton}
                        >
                            <CopyIcon />
                        </Button>
                    </div>
                </div>
                <div className={styles.feeSection}>
                    <p className={styles.feeLabel}>Doctoral Fee</p>
                    <div className={styles.feeContent}>
                        <div className={styles.feeAmount}>
                            {calculateFeeResult?.data?.[0]?.doctoral
                                ? calculateFeeResult?.data?.[0]?.doctoral
                                : showMoneyInAppFormat(0)}
                        </div>
                        <Button
                            onClick={() => handleFeeCopied("doctoral")}
                            aria-label="copyDoctoralFee"
                            variant="normal"
                            className={styles.copyButton}
                        >
                            <CopyIcon />
                        </Button>
                    </div>
                </div>
                {calculateFeeResult && (
                    <Button
                        onClick={() => {
                            setShowAlertDialog(true);
                        }}
                    >
                        Autofill Fees Boxes
                    </Button>
                )}
            </Dialog>
            <AlertDialog
                open={showAlertDialog}
                onClose={() => setShowAlertDialog(false)}
                title="Auto fill boxes?"
                description="Clicking confirm will autofill the masters fee and doctor fee boxes for this client."
                saveText="Confirm"
                cancelText="Cancel"
                onSaveClick={() => {
                    setAutoFillFees(true);
                    setShowAlertDialog(false);
                    setShowCalculateFeeModal(false);
                }}
            />

            <Button onClick={() => setShowCalculateFeeModal(true)}>
                Calculate Fees
            </Button>
        </>
    );
}
